/* eslint-disable lumapps/no-classname-strings */
/* eslint-disable no-use-before-define */
import React, { ReactNode } from 'react';

import noop from 'lodash/noop';

import { classnames } from '@lumapps/classnames';
import {
    mdiCheckboxBlankOutline,
    mdiCheckboxMarked,
    mdiCheckCircle,
    mdiChevronRight,
    mdiCircleOutline,
    mdiRadioboxBlank,
    mdiRadioboxMarked,
} from '@lumapps/lumx/icons';
import { Icon, IconProps, Size, Text } from '@lumapps/lumx/react';
import { MovingFocusProvider } from '@lumapps/lumx/react/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useOption } from '@lumapps/utils/hooks/useOption';

import './index.scss';

export const CLASSNAME = 'picker-list';

export type PickerListProps<I> = {
    'aria-labelledby': string;
    items: I[];
    disabledItems?: I[];
    alreadyUsedItems?: I[];
    itemRenderer: React.FC<I>;
    itemKeySelector(item: I): string | number;
    itemIdSelector?(item: I): string;
    itemNavigableSelector?(item: I): boolean;
    itemSelectableSelector?(item: I): boolean;
    onNavigate?(item: I): void;
    afterList?: React.ReactNode;
    className?: string;
    /** changes the icon from rounded check to checked radio button is single selection */
    isRadio?: boolean;
} & (
    | {
          isMultiple?: false;
          selectedItem?: I;
          setSelectedItem(item?: I): void;
          selectedItems?: never;
          setSelectedItems?: never;
      }
    | {
          isMultiple: true;
          selectedItem?: never;
          setSelectedItem?: never;
          selectedItems: I[];
          setSelectedItems(item?: I[]): void;
      }
);

/**
 * List-like component implementing the "listbox" role and the roving tab index pattern.
 *
 * @family Lists
 */
export const PickerList = <I,>(props: PickerListProps<I>) => {
    const {
        isMultiple = false,
        items,
        setSelectedItems = noop, // to satisfy typescript when isMultiple is false
        selectedItems,
        setSelectedItem = noop, // to satisfy typescript when isMultiple is true
        selectedItem,
        disabledItems = [],
        alreadyUsedItems = [],
        itemKeySelector,
        itemIdSelector,
        itemRenderer: ItemRenderer,
        itemNavigableSelector,
        itemSelectableSelector,
        onNavigate,
        isRadio = false,
        afterList,
        className,
        ...forwardedProps
    } = props;

    const isInList = (list: I[], item: I) => {
        return list.findIndex((i) => itemKeySelector(i) === itemKeySelector(item)) > -1;
    };

    const onSelect = (item: I) => () => {
        if (isMultiple) {
            const nextSelectedItems = isInList(selectedItems as I[], item)
                ? (selectedItems as I[]).filter((i) => i !== item)
                : [...(selectedItems as I[]), item];

            setSelectedItems(nextSelectedItems);
        } else {
            setSelectedItem(item);
        }
    };

    return (
        <MovingFocusProvider options={{ direction: 'both', loopAround: true }}>
            <ul
                className={classnames(CLASSNAME, className)}
                role="listbox"
                aria-multiselectable={isMultiple}
                {...forwardedProps}
            >
                {items?.map((item) => (
                    <PickerListItem
                        id={itemIdSelector && itemIdSelector(item)}
                        isSelected={isMultiple ? isInList(selectedItems as I[], item) : item === selectedItem}
                        isDisabled={isInList(disabledItems, item)}
                        isAlreadyUsed={isInList(alreadyUsedItems, item)}
                        selectItem={onSelect(item)}
                        isInMultipleSelect={isMultiple}
                        // Select on focus on single selection and when navigation is not enabled
                        selectOnFocus={!isMultiple && !onNavigate}
                        isRadio={isRadio}
                        isNavigable={itemNavigableSelector?.(item) ?? false}
                        isSelectable={itemSelectableSelector?.(item) ?? true}
                        onNavigate={() => onNavigate?.(item)}
                        key={itemKeySelector(item)}
                    >
                        <ItemRenderer {...(item as I & React.JSX.IntrinsicAttributes)} />
                    </PickerListItem>
                ))}
                {afterList}
            </ul>
        </MovingFocusProvider>
    );
};

PickerList.displayName = 'PickerList';

interface PickerListItemProps {
    id?: string;
    isSelected: boolean;
    isInMultipleSelect: boolean;
    isDisabled: boolean;
    isAlreadyUsed: boolean;
    isRadio: boolean;
    selectItem(): void;
    children?: ReactNode;
    selectOnFocus: boolean;
    isNavigable: boolean;
    isSelectable: boolean;
    onNavigate(): void;
}

/**
 * ListItem-like component implementing the "option" role and the rolling tab index pattern.
 */
export const PickerListItem: React.FC<PickerListItemProps> = ({
    isSelected,
    selectItem,
    children,
    isInMultipleSelect,
    isSelectable,
    isNavigable,
    onNavigate,
    isDisabled,
    isAlreadyUsed,
    id,
    isRadio,
    selectOnFocus,
}) => {
    const { translateKey } = useTranslate();

    let icon;

    if (isNavigable) {
        icon = mdiChevronRight;
    } else if (isInMultipleSelect) {
        icon = isSelected ? mdiCheckboxMarked : mdiCheckboxBlankOutline;
    } else if (isRadio) {
        icon = isSelected ? mdiRadioboxMarked : mdiRadioboxBlank;
    } else {
        icon = isSelected ? mdiCheckCircle : mdiCircleOutline;
    }

    const selectionIconProps: IconProps = isSelected ? { icon } : { icon, color: 'dark', colorVariant: 'N' };

    const ref = React.useRef<HTMLDivElement>(null);

    const selectOrNavigate = (isNavigable && onNavigate) || (isSelectable && selectItem) || undefined;

    const isItemDisabled = isDisabled || (!isNavigable && !isSelectable);

    // ARIA option & roving tab index
    const optionProps = useOption({
        ref,
        selectItem: selectOrNavigate,
        isDisabled: isItemDisabled || isAlreadyUsed,
        selectOnFocus,
    });

    return (
        // As the ul has role of listbox, the li must have a presentation role and the actual items must have the option role
        <li className={`${CLASSNAME}-item`} role="presentation">
            <div
                ref={ref}
                className={classnames(
                    `${CLASSNAME}-item__link`,
                    isSelected && `${CLASSNAME}-item__link--is-selected`,
                    !isItemDisabled && `${CLASSNAME}-item__link--is-clickable`,
                    isItemDisabled && !isAlreadyUsed && `${CLASSNAME}-item__link--is-disabled`,
                )}
                // ARIA option role.
                role="option"
                // ARIA selected.
                aria-selected={isSelected}
                // ARIA checked.
                aria-disabled={isItemDisabled || undefined}
                {...optionProps}
                aria-labelledby={id && `option-${id}-title`}
                aria-describedby={id && `option-${id}-description`}
            >
                <div className={`${CLASSNAME}-item__content`}>{children}</div>

                <div className={`${CLASSNAME}-item__after`}>
                    {isAlreadyUsed ? (
                        <Text as="span" typography="subtitle1">
                            {translateKey(GLOBAL.ALREADY_ADDED)}
                        </Text>
                    ) : (
                        (isNavigable || isSelectable) && <Icon size={Size.s} {...selectionIconProps} />
                    )}
                </div>
            </div>
        </li>
    );
};
PickerListItem.displayName = 'PickerListItem';
